import {
  CONNECTION_CATEGORY_KAFKA,
  CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT,
  userDataCategories,
} from 'js/constants/connectionCategories';
import {
  ARCHIVAL_STATUS_ARCHIVED,
  ARCHIVAL_STATUS_RESTORING,
} from 'js/constants/pipelineConstants';
import { FlowTriggerHelper } from 'js/components/data_sources/add_ds_form/MonitoringInterval/FlowTriggerHelper';

export const shouldRenderWebhookAction = (data: any) => {
  return data?.source_type === 'nexla_rest';
};

export const shouldRenderExternalTriggerAction = (data: any) => {
  return !!data?.['adaptive_flow'];
};

export const shouldRenderCronAction = (data: any, connectionCategory: string) => {
  return (
    !shouldRenderExternalTriggerAction(data) &&
    connectionCategory !== CONNECTION_CATEGORY_TEMPLATIZED_EMAIL_SCRIPT &&
    !!data?.source_config?.['start.cron']
  );
};

// True when a source can never offer Run Now regardless of schedule — no source data,
// Kafka/user-data categories, archived/restoring, triggers, or API server
// nodes. Exported separately from shouldRenderRunAction so callers that grant a schedule
// exemption (e.g. Capsule flows, NEX-21660) can still apply these exclusions on their own.
export const isRunIncompatibleSource = (data: any, connectionCategory?: string) => {
  if (!data) return true;

  // archived/restoring flows cannot run
  const archivalStatus = data?.archival_status;
  if (archivalStatus === ARCHIVAL_STATUS_ARCHIVED || archivalStatus === ARCHIVAL_STATUS_RESTORING) {
    return true;
  }

  // Some sources carry the category on connection_type instead of connection_category —
  // check both field names.
  const runIncompatibleCategories = [...userDataCategories, CONNECTION_CATEGORY_KAFKA];
  if (
    runIncompatibleCategories.includes(data.connection_category) ||
    runIncompatibleCategories.includes(data.connection_type)
  ) {
    return true;
  }

  if (data?.source_type === 'nexla_rest') {
    return true;
  }

  if (data?.['adaptive_flow'] === true) {
    return true;
  }

  if (connectionCategory && runIncompatibleCategories.includes(connectionCategory)) {
    return true;
  }

  // Completion triggers that start this source (not ones where it starts another flow)
  if (FlowTriggerHelper.getInboundTriggers(data).length > 0) {
    return true;
  }

  if (['api_server', 'api_target'].includes(data?.node_type)) {
    return true;
  }

  return false;
};

const hasUsableRecurringSchedule = (data: any) => {
  const cronExpression = data?.source_config?.['start.cron'];

  // Return false if no cron is configured
  if (!cronExpression) {
    return false;
  }

  // Return false for "once" cron
  if (cronExpression === 'once') {
    return false;
  }

  // Return false for continuous cron patterns (every minute)
  if (cronExpression === '0 0/1 * 1/1 * ? *') {
    return false;
  }

  return true;
};

export const shouldRenderRunAction = (data: any, connectionCategory?: string) =>
  !isRunIncompatibleSource(data, connectionCategory) && hasUsableRecurringSchedule(data);

export const shouldRenderExpandActions = (data: any, connectionCategory: string) => {
  return (
    shouldRenderWebhookAction(data) ||
    shouldRenderExternalTriggerAction(data) ||
    shouldRenderCronAction(data, connectionCategory) ||
    shouldRenderRunAction(data, connectionCategory)
  );
};
